import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

/** The region a number without a country code is read in. */
export const DEFAULT_PHONE_REGION = 'GB';

const phoneUtil = PhoneNumberUtil.getInstance();

// Digits and the separators a number is written with, a `+` only at its start,
// and nothing else: an extension, a second number or a note is never joined
// onto the number
const PHONE_CHARACTERS = /^\s*\+?[\d\s\-.()]*$/;

// `(0)`: the UK trunk 0, written at the start or after the country code
// (`+44 (0)7911 …`); anywhere else it is not part of the number
const TRUNK_ZERO_IN_BRACKETS = /^(\s*(?:(?:\+|00)\s*\d{1,3}\s*)?)\(0\)/;

const toE164 = (candidate: string): string | undefined => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(
      candidate,
      DEFAULT_PHONE_REGION,
    );
    return phoneUtil.isValidNumber(number)
      ? phoneUtil.format(number, PhoneNumberFormat.E164)
      : undefined;
  } catch {
    return undefined;
  }
};

/** The country codes (1 to 3 digits) that `digits` may start with. */
const countryCodePrefixes = (digits: string) =>
  [1, 2, 3]
    .map((length) => digits.slice(0, length))
    .filter(
      (code) =>
        code.length > 0 &&
        phoneUtil.getRegionCodeForCountryCode(Number(code)) !== 'ZZ',
    );

/**
 * Repairs of an international number (`digits` without the `+`) that a partner
 * or a form got wrong: the national trunk 0 kept after the country code
 * (`+44 07911…`, also what the UK input mask produces) and the country code
 * written twice (`4444 7911…`).
 */
const repairInternational = (digits: string) =>
  countryCodePrefixes(digits).flatMap((code) => {
    const rest = digits.slice(code.length);
    return [
      ...(rest.startsWith('0') ? [code + rest.slice(1)] : []),
      ...(rest.startsWith(code) ? [rest] : []),
    ];
  });

/**
 * Reads a telephone number in any of the formats members and partners send and
 * returns it in strict E.164 (`+447911123456`), or `undefined` when no reading
 * of it is a valid number.
 *
 * A number without a country code is read as a UK one (`07911 123456`). The
 * readings are tried in order and the first that google-libphonenumber
 * (the library the Platform validates with) accepts as valid wins, so a repair
 * is only ever made when it produces a real number:
 *
 * - `+44 7911 123456`, `0044 7911 123456`, `+44 (0)7911 123456` as written
 * - `447911123456` (Healix: the country code without the `+`)
 * - `07911 123456`, `7911 123456` (a UK number, with or without the trunk 0)
 * - `+44 07911 123456`, `4407911123456` (the trunk 0 kept after the code)
 * - `4444 7911 123456` (the country code written twice)
 * - `+07911 123456` (a UK number with a stray `+`)
 * - `353 87 123 4567` (another country's code without the `+`)
 *
 * Separators (spaces, dashes, dots, brackets) are ignored. Anything else — an
 * extension (`07911 12345 ext 6`), a second number, a note — has no reading:
 * dropping it would join its digits onto the number. A number with a digit
 * missing or added cannot be repaired and is not guessed at.
 */
export const normalizePhoneNumber = (value: string): string | undefined => {
  if (!PHONE_CHARACTERS.test(value)) {
    return undefined;
  }
  const text = value.replace(TRUNK_ZERO_IN_BRACKETS, '$1').trim();
  if (text.includes('(0)')) {
    return undefined;
  }
  let digits = text.replace(/\D/g, '');
  let international = /^[^\d]*\+/.test(text);
  if (!international && digits.startsWith('00')) {
    international = true;
    digits = digits.slice(2);
  }
  if (digits === '') {
    return undefined;
  }

  const candidates = international
    ? [
        `+${digits}`,
        ...repairInternational(digits).map((repaired) => `+${repaired}`),
        digits, // a national number with a stray `+`
      ]
    : digits.startsWith('0')
      ? [digits]
      : [
          // a leading country code wins over a UK number missing its trunk 0
          // only for the UK code itself (`447911…` is Healix's format)
          ...(digits.startsWith('44')
            ? [digits, ...repairInternational(digits)].map((d) => `+${d}`)
            : []),
          digits,
          `+${digits}`,
          ...repairInternational(digits).map((repaired) => `+${repaired}`),
        ];

  for (const candidate of candidates) {
    const e164 = toE164(candidate);
    if (e164) {
      return e164;
    }
  }
  return undefined;
};
